import React from 'react';
import { ProfileImage } from 'components/ProfileImage';
import { ThemedHevyProLogo } from 'components/svg/ThemedHevyProLogo';
import Link from 'next/link';
import { stores } from 'state/stores';
import { PrimaryButton } from 'styleguide/Buttons';
import { P3, P2, H5 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { NavBarRoute } from '../NavBarRoute';
import styled, { ThemeProps } from 'styled-components';
import { colors } from 'styleguide/colors';
import { MIN_WIDTH_FOR_SIDEBAR_NAVIGATION, themeState } from 'styleguide/theme';
import { SearchInput } from 'components/SearchInput';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { NavBarUserSearchViewModel } from './NavBarUserSearchViewModel';
import { observer } from 'mobx-react-lite';
import { ControlledMenu, useMenuState } from '@szhsin/react-menu';
import { ClipLoader } from 'react-spinners';
import { SearchUser } from 'hevy-shared';
import { modal } from 'components/Modals/ModalManager';
import { DividerLine } from 'styleguide/DividerLine';
import { autorun } from 'mobx';
import { LogoutButton } from './LogoutButton';
import { FlexRow } from 'styleguide/Row';
import { Theme } from 'styleguide/types';
import { t } from 'translations/t';

const CtaContainer = styled(FlexRow)`
  justify-content: space-between;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${CornerRadius.md}px;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.greyHiperLight : colors.greyDark};
`;

export interface NavBarRoutesProps {
  pathname: string;
  showProCta: boolean;
  isSettingsEnabled: boolean;
  vm: NavBarUserSearchViewModel;
}

const SearchResultsContainer = styled.div`
  overflow-y: auto;
  min-width: min(332px, 90vw);
  max-height: min(300px, 90vh);
`;
const SearchResultsInnerContainer = styled.div`
  margin-inline-start: ${Spacing.sm}px;
  margin-inline-end: ${Spacing.sm}px;
  padding-top: ${Spacing.sm}px;
  padding-bottom: ${Spacing.sm}px;
`;

const NavBarTopContainer = styled(View)`
  padding: ${Spacing.md}px;
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    padding: ${Spacing.sm}px;
  }
`;

const NavBarBottomContainer = styled(View)``;

const UserSearchResult = ({ user }: { user: SearchUser }) => {
  const [isHovering, setIsHovering] = React.useState(false);
  const hoverBackgroundColor =
    themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyUltraDark;
  return (
    <Link
      href={`/user/${user.username}`}
      style={{ textDecoration: 'none' }}
      onClick={() => {
        modal.isMobileNavBarModalOpen = false;
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          padding: Spacing.sm,
          backgroundColor: isHovering ? hoverBackgroundColor : undefined,
          borderRadius: CornerRadius.sm,
          cursor: 'pointer',
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <ProfileImage diameter={40} source={user.profile_pic} />
        <View style={{ justifyContent: 'center', marginInlineStart: Spacing.sm }}>
          <P2>{user.username}</P2>
          {user.full_name && <P2>{user.full_name}</P2>}
        </View>
      </View>
    </Link>
  );
};

const SearchResultsDropdown = observer(({ vm }: { vm: NavBarUserSearchViewModel }) => {
  return (
    <SearchResultsContainer style={{ backgroundColor: themeState.current.background }}>
      <SearchResultsInnerContainer>
        {vm.isLoadingResults ? (
          <ClipLoader size={30} color={colors.primary} loading={true} />
        ) : (
          <>
            {vm.results.length === 0 && (
              <>
                <P2 style={{ flexWrap: 'wrap', flex: 1 }}>
                  {t('web.navBar.cantFind', { searchString: vm.searchString })}
                </P2>
                <P3>{t('web.navBar.maybeArentOnHevy')}</P3>
              </>
            )}
            {vm.results.map(user => {
              return <UserSearchResult key={user.id} user={user} />;
            })}
          </>
        )}
      </SearchResultsInnerContainer>
    </SearchResultsContainer>
  );
});

export const NavBarMainContent = observer(
  ({ pathname, showProCta, isSettingsEnabled, vm }: NavBarRoutesProps) => {
    const menuRef = React.useRef(null);
    const [menuState, toggleMenu] = useMenuState({ transition: true });
    autorun(() => {
      vm.showMenu ? toggleMenu(true) : toggleMenu(false);
    });
    return (
      <View style={{ justifyContent: 'space-between', flex: 1 }}>
        <NavBarTopContainer>
          <SearchInput
            style={{ marginBottom: Spacing.md, width: '100%' }}
            value={vm.searchString}
            type={'primary'}
            placeholder={t('web.navBar.searchUsersPlaceholder')}
            onChange={vm.onSearchStringChanged}
          />
          <View ref={(ref: any) => (menuRef.current = ref)} />
          <ControlledMenu
            menuStyle={{ padding: 0, margin: 0, overflow: 'hidden', borderRadius: CornerRadius.md }}
            anchorRef={menuRef}
            captureFocus={false}
            direction="bottom"
            {...menuState}
          >
            <SearchResultsDropdown vm={vm} />
          </ControlledMenu>

          <NavBarRoute
            title={t('global.feed')}
            iconType="home"
            isSelected={pathname === '/'}
            path="/"
          />
          <NavBarRoute
            title={t('global.routines')}
            iconType="routine"
            isSelected={
              pathname.startsWith('/routine') ||
              pathname.startsWith('/create-routine') ||
              pathname.startsWith('/edit-routine')
            }
            path="/routines"
          />

          <NavBarRoute
            title={t('global.exercises')}
            iconType="exercise"
            isSelected={pathname.startsWith('/exercise')}
            path="/exercise"
          />

          <NavBarRoute
            // Trainer is a brand name, it should not be translated
            title="Trainer"
            iconType="trainer"
            isSelected={pathname.startsWith('/trainer')}
            path="/trainer"
          />

          {stores.account.is_coached && (
            <NavBarRoute
              title={t('web.navBar.coach')}
              iconType="coach"
              isSelected={pathname === '/coach'}
              path="/coach"
            />
          )}

          <NavBarRoute
            title={t('web.navBar.profile')}
            iconType="profile"
            isSelected={pathname === '/profile'}
            path="/profile"
          />

          {isSettingsEnabled && (
            <NavBarRoute
              title={t('web.navBar.settings')}
              iconType="settings"
              isSelected={pathname.startsWith('/settings')}
              path="/settings"
            />
          )}
        </NavBarTopContainer>
        <NavBarBottomContainer>
          {showProCta && (
            <View style={{ padding: Spacing.md }}>
              <Link href="/plans" style={{ textDecoration: 'none' }}>
                <CtaContainer>
                  <ThemedHevyProLogo />
                  <PrimaryButton size="sm" title={t('web.navBar.unlock')} />
                </CtaContainer>
              </Link>
            </View>
          )}
          <DividerLine />
          <FlexRow style={{ justifyContent: 'space-between', padding: Spacing.md }}>
            <FlexRow style={{ gap: Spacing.md }}>
              <ProfileImage source={stores.account.profile_pic} diameter={34} />
              <H5>{stores.account.full_name || stores.account.username}</H5>
            </FlexRow>

            <LogoutButton />
          </FlexRow>
        </NavBarBottomContainer>
      </View>
    );
  },
);
